/**
 * Centralized Provider Color System
 * Implements REQ-012-HYBRID: Consistent color-coding for AWS, Azure, and GCP.
 *
 * AWS:   Orange (#FF9900)
 * Azure: Blue   (#0089D6)
 * GCP:   Green  (#34A853)
 *
 * This module is the single source of truth for all provider-related colors
 * used across the dashboard, charts, badges, and UI components.
 *
 * The brand hues are kept (REQ-012-HYBRID: telling three clouds apart at a
 * glance in a multi-cloud list is worth a hue), but the Tailwind classes now
 * use the same `bg-<hue>-500/10 text-<hue>-600 dark:text-<hue>-400` tint
 * formula as every other badge in the app, instead of the old solid
 * `bg-<hue>-100 text-<hue>-800` pairs. Same information, a quarter of the
 * saturation - the old solid fills were a large part of why the UI read as
 * "too coloured". Azure moved blue->sky and GCP green->emerald so the app
 * carries one blue-ish and one green-ish family rather than two of each.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Cloud provider identifiers used throughout the color system */
export type ProviderColorKey = 'aws' | 'azure' | 'gcp';

/** Extended key that includes the "all providers" option */
export type ProviderColorKeyWithAll = ProviderColorKey | 'all';

/** Complete color palette for a single provider */
export interface ProviderColorPalette {
  /** Primary brand color (hex) */
  primary: string;
  /** Lighter background tint for card accents (hex with alpha) */
  backgroundTint: string;
  /** Background color for light mode cards (hex solid) */
  backgroundLight: string;
  /** Background color for dark mode cards (hex solid) */
  backgroundDark: string;
  /** Border color for active/hover states */
  border: string;
  /** Border color for dark mode */
  borderDark: string;
  /** Tailwind-compatible background class (light mode) */
  bgClass: string;
  /** Tailwind-compatible background class (dark mode) */
  bgDarkClass: string;
  /** Tailwind-compatible text class */
  textClass: string;
  /** Tailwind-compatible text class (dark mode) */
  textDarkClass: string;
  /** Tailwind-compatible badge class (background + text) */
  badgeClass: string;
  /** Tailwind progress bar / solid fill class */
  fillClass: string;
  /** Chart color (same as primary, used for chart libraries) */
  chart: string;
}

// ---------------------------------------------------------------------------
// Primary Color Constants
// ---------------------------------------------------------------------------

/**
 * Primary brand colors for each cloud provider.
 * These are the canonical hex values specified in the design system.
 */
export const PROVIDER_COLORS = {
  aws: '#FF9900',
  azure: '#0089D6',
  gcp: '#34A853',
} as const;

/**
 * Color for the "All Providers" option (neutral gray).
 */
export const ALL_PROVIDERS_COLOR = '#6B7280';

/**
 * Full color map including the "all" option.
 */
export const PROVIDER_COLORS_WITH_ALL = {
  all: ALL_PROVIDERS_COLOR,
  ...PROVIDER_COLORS,
} as const;

// ---------------------------------------------------------------------------
// Extended Color Palettes
// ---------------------------------------------------------------------------

/**
 * Complete color palette for each provider including all variants.
 * Use this when you need background tints, borders, or Tailwind classes.
 */
export const PROVIDER_PALETTES: Record<ProviderColorKey, ProviderColorPalette> = {
  aws: {
    primary: '#FF9900',
    backgroundTint: '#FF990015',
    backgroundLight: '#FFF7ED',
    backgroundDark: '#431407',
    border: '#FDBA74',
    borderDark: '#92400E',
    bgClass: 'bg-orange-500/10',
    bgDarkClass: '',
    textClass: 'text-orange-600',
    textDarkClass: 'dark:text-orange-400',
    badgeClass: 'bg-orange-500/10 text-orange-600 dark:text-orange-400',
    fillClass: 'bg-orange-500',
    chart: '#FF9900',
  },
  azure: {
    primary: '#0089D6',
    backgroundTint: '#0089D615',
    backgroundLight: '#EFF6FF',
    backgroundDark: '#172554',
    border: '#60A5FA',
    borderDark: '#1E40AF',
    bgClass: 'bg-sky-500/10',
    bgDarkClass: '',
    textClass: 'text-sky-600',
    textDarkClass: 'dark:text-sky-400',
    badgeClass: 'bg-sky-500/10 text-sky-600 dark:text-sky-400',
    fillClass: 'bg-sky-500',
    chart: '#0089D6',
  },
  gcp: {
    primary: '#34A853',
    backgroundTint: '#34A85315',
    backgroundLight: '#F0FDF4',
    backgroundDark: '#052E16',
    border: '#86EFAC',
    borderDark: '#166534',
    bgClass: 'bg-emerald-500/10',
    bgDarkClass: '',
    textClass: 'text-emerald-600',
    textDarkClass: 'dark:text-emerald-400',
    badgeClass: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    fillClass: 'bg-emerald-500',
    chart: '#34A853',
  },
} as const;

// ---------------------------------------------------------------------------
// Chart Colors (ordered array for stacked/pie charts)
// ---------------------------------------------------------------------------

/**
 * Provider chart colors in consistent order: AWS, Azure, GCP.
 * Use with charting libraries (Recharts, Chart.js, etc.)
 */
export const PROVIDER_CHART_COLORS: readonly string[] = [
  PROVIDER_COLORS.aws,
  PROVIDER_COLORS.azure,
  PROVIDER_COLORS.gcp,
];

/**
 * Provider chart color map for lookup by key.
 */
export const PROVIDER_CHART_COLOR_MAP: Record<ProviderColorKey, string> = {
  aws: PROVIDER_COLORS.aws,
  azure: PROVIDER_COLORS.azure,
  gcp: PROVIDER_COLORS.gcp,
};

// ---------------------------------------------------------------------------
// Helper Functions
// ---------------------------------------------------------------------------

/**
 * Get the primary brand color for a given provider.
 *
 * @param provider - The cloud provider key
 * @returns Hex color string (e.g., '#FF9900')
 *
 * @example
 * getProviderColor('aws') // '#FF9900'
 * getProviderColor('azure') // '#0089D6'
 */
export function getProviderColor(provider: ProviderColorKeyWithAll): string {
  return PROVIDER_COLORS_WITH_ALL[provider] ?? ALL_PROVIDERS_COLOR;
}

/**
 * Get the full color palette for a provider.
 *
 * @param provider - The cloud provider key (aws, azure, gcp)
 * @returns The complete ProviderColorPalette or undefined for 'all'
 */
export function getProviderPalette(provider: ProviderColorKey): ProviderColorPalette {
  return PROVIDER_PALETTES[provider];
}

/**
 * Get Tailwind-compatible background class for a provider.
 * Includes both light and dark mode classes.
 *
 * @param provider - The cloud provider key
 * @returns Combined Tailwind class string (e.g., 'bg-orange-50 dark:bg-orange-950')
 *
 * @example
 * <div className={getProviderBgClass('aws')} />
 * // → 'bg-orange-50 dark:bg-orange-950'
 */
export function getProviderBgClass(provider: ProviderColorKey): string {
  const palette = PROVIDER_PALETTES[provider];
  if (!palette) return 'bg-gray-50 dark:bg-gray-900';
  return `${palette.bgClass} ${palette.bgDarkClass}`;
}

/**
 * Get Tailwind-compatible text class for a provider.
 * Includes both light and dark mode classes.
 *
 * @param provider - The cloud provider key
 * @returns Combined Tailwind class string (e.g., 'text-orange-600 dark:text-orange-400')
 *
 * @example
 * <span className={getProviderTextClass('aws')}>AWS</span>
 * // → 'text-orange-600 dark:text-orange-400'
 */
export function getProviderTextClass(provider: ProviderColorKey): string {
  const palette = PROVIDER_PALETTES[provider];
  if (!palette) return 'text-gray-600 dark:text-gray-400';
  return `${palette.textClass} ${palette.textDarkClass}`;
}

/**
 * Get Tailwind-compatible badge class for a provider.
 * Provides background + text colors for badge/chip components.
 *
 * @param provider - The cloud provider key
 * @returns Combined Tailwind class string for badges
 *
 * @example
 * <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${getProviderBadgeClass('gcp')}`}>
 *   GCP
 * </span>
 */
export function getProviderBadgeClass(provider: ProviderColorKey): string {
  const palette = PROVIDER_PALETTES[provider];
  if (!palette) return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  return palette.badgeClass;
}

/**
 * Get Tailwind-compatible progress bar / fill class for a provider.
 *
 * @param provider - The cloud provider key (case-insensitive)
 * @returns Tailwind background class for solid fills (e.g., 'bg-orange-500')
 *
 * @example
 * <div className={`h-2 ${getProviderFillClass('aws')}`} style={{ width: '52%' }} />
 */
export function getProviderFillClass(provider: string): string {
  const key = provider.toLowerCase() as ProviderColorKey;
  const palette = PROVIDER_PALETTES[key];
  if (!palette) return 'bg-gray-500';
  return palette.fillClass;
}

/**
 * Get the background tint color for a provider (for inline styles).
 * Useful for icon backgrounds and card accents with transparency.
 *
 * @param provider - The cloud provider key
 * @returns Hex color with alpha (e.g., '#FF990015')
 */
export function getProviderBackgroundTint(provider: ProviderColorKey): string {
  const palette = PROVIDER_PALETTES[provider];
  if (!palette) return '#6B728015';
  return palette.backgroundTint;
}

/**
 * Get the border color for a provider (for inline styles or active states).
 *
 * @param provider - The cloud provider key
 * @returns Hex color string for borders
 */
export function getProviderBorderColor(provider: ProviderColorKey): string {
  const palette = PROVIDER_PALETTES[provider];
  if (!palette) return '#D1D5DB';
  return palette.border;
}
